import { Alert, Button, Spin } from 'antd';
import { RightOutlined, DownOutlined } from '@ant-design/icons';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import useMountEffect from '../../hooks/useMountEffect';
import DateTimeHover from '../date_time_component/DateTime';
import { AOPActorName } from '../aop_builder/AOPEventSentence';
import { EmptyState } from '../empty_state/EmptyState';
import OscarMark from '../../ai_agent/components/shared/OscarMark';
import AOPLoadFailed from '../aop_builder/AOPLoadFailed';
import { getEntityById, getWorkOrderTimeZoneId, nullSafeGet } from '../../utils/DataAccessUtils';
import { AOPExecution, pageRows } from '../../types/AOPExecutionTypes';
import { readWorkOrderExecutions } from '../../thunks/agent_executions_thunks';
import { AOP_RUN_STATES, runStateOf } from '../aop_builder/utils/AOPExecutionStates';
import RadarSessionTimeline from './RadarSessionTimeline';

const MARK_SIZE = 20;

/**
 * One page of sessions, and the rest asked for.
 *
 * A work order reaching 50 runs is far-fetched, but walking the cursor to the
 * end on open read sessions nobody had scrolled to and stopped at a fixed
 * number of pages whether or not that was the end — so a work order past the
 * cap lost its oldest sessions with nothing said.
 */
const PAGE_SIZE = 50;

/**
 * Radar Activity on one work order: what Radar did, and in which session.
 *
 * A session is an execution — one run of one published version against this
 * work order. A work order can be reopened and picked up again, so there can be
 * several; the newest is open and the rest are behind "Past sessions", because
 * what Radar is doing now is what somebody opening this tab came for.
 *
 * A run carries its procedure's name, so nothing here looks one up. It is the
 * name as it is now rather than as it was at the time, and it survives the
 * procedure being deleted, which is why a finished run can still be labelled.
 */

/**
 * What the newest event says a person did.
 *
 * `requested_by` rides on `paused`, `resumed`, and a `finished` that a buyer
 * cancelled — nothing else — so the verb and the name come off the same event.
 */
const OVERRIDE_VERBS: { [kind: string]: string } = {
	paused: 'Paused',
	resumed: 'Resumed',
	finished: 'Cancelled',
};

/** The run's state, its procedure, and who ended it — the line above the steps. */
const SessionHeader: FC<{
	execution: AOPExecution;
	procedureName: string | null;
	timeZoneId: string;
}> = ({ execution, procedureName, timeZoneId }) => {
	const chrome = AOP_RUN_STATES[runStateOf(execution)];
	const latest = execution.latest;
	const snapshot = latest ? latest.snapshot : null;
	const requestedBy = snapshot ? snapshot.requested_by : undefined;
	const verb = latest ? OVERRIDE_VERBS[latest.kind] : undefined;

	/*
	  An override is dated from its own event, not from the run.

	  Somebody can pause a run hours after it started, so pairing their name
	  with `started_at` had the line say they acted at a time they did not. The
	  name and the moment have to come off the same event — which is also why
	  the verb does, rather than being inferred from the state chip: that chip
	  says "Active" after a resume, and "By <name>" beside it read as if they
	  had started the run.

	  One branch yields both, so the label and the timestamp cannot come apart
	  again the way they did.
	*/
	/*
	  `actor` rather than a name in the string, for the same reason the feed
	  lines carry one: the service identifies the person by their address, and
	  `AOPEventSentence` is what turns an address into whoever it belongs to.
	*/
	const line =
		Boolean(requestedBy) && Boolean(verb)
			? { lead: `${verb} by`, actor: requestedBy, at: latest.occurred_at }
			: execution.finished_at
			? { lead: 'Finished', actor: null, at: execution.finished_at }
			: { lead: 'Started', actor: null, at: execution.started_at };

	return (
		<div className="flex items-center gap-s bg-grey-1 px-m py-s">
			<span className={chrome.textClass}>
				<OscarMark size={MARK_SIZE} animation={chrome.isAnimated ? 'flap' : undefined} />
			</span>
			<span className={`bodymedium ${chrome.textClass}`}>{chrome.label}</span>
			<span className="bodymedium text-character-primary">
				{procedureName ? procedureName : 'Radar'}:
			</span>
			<span className="bodyregular text-character-secondary">
				{line.lead}{' '}
				{line.actor ? (
					<>
						<AOPActorName email={line.actor} /> at{' '}
					</>
				) : null}
				{/*
				  The site's zone, like every line of the timeline below. Formatting
				  this one in the browser's instead put two clocks on the same
				  session for anybody not sitting at the site.
				*/}
				<DateTimeHover timestamp={line.at} timeZone={timeZoneId} showDate dateFormat="h:mm A z" />
			</span>
		</div>
	);
};

const WorkOrdersDetailRadarActivityPage: FC<any> = ({ match, timeZoneId, loadExecutions }) => {
	const workOrderId = useMemo(() => parseInt(nullSafeGet('params.id', match), 10), [match]);

	const [executions, setExecutions] = useState<AOPExecution[]>([]);
	// Where the next page of sessions starts, or null once they are all here.
	const [cursor, setCursor] = useState<string | null>(null);
	const [openPastId, setOpenPastId] = useState<string | null>(null);
	const [arePastSessionsOpen, setArePastSessionsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Reads one page of this work order's sessions, newest first.
	 *
	 * `known` is what is already held rather than a functional update: the
	 * procedure names are asked for off the back of the same answer, and which
	 * of them are new is decided against the sessions already read.
	 */
	const load = useCallback(
		(after: string | null, known: AOPExecution[]) =>
			loadExecutions(workOrderId, after, PAGE_SIZE).then((page) => {
				const rows = pageRows<AOPExecution>(page);

				setExecutions(after === null ? rows : known.concat(rows));
				setCursor(page.next_cursor ? page.next_cursor : null);
				// Cleared on success: a page that failed and then went through left
				// the sessions on screen under a message saying they could not load.
				setError(null);
			}),
		[loadExecutions, workOrderId]
	);

	useMountEffect(() => {
		load(null, [])
			.catch(() => setError('Could not load what Radar has done on this work order.'))
			.then(() => setIsLoading(false));
	});

	const onLoadMore = useCallback(() => {
		setIsLoadingMore(true);
		load(cursor, executions)
			.catch(() => setError('Could not load the rest of the sessions on this work order.'))
			.then(() => setIsLoadingMore(false));
	}, [cursor, executions, load]);

	if (isLoading) {
		return (
			<div className="flex justify-center p-xs">
				<Spin />
			</div>
		);
	}

	if (executions.length === 0) {
		/*
		  A read that failed is not "Radar has not been here". The two were being
		  shown one above the other, and the second is a claim about this work
		  order that the failed read is exactly what could not establish.
		*/
		if (error) {
			return (
				<div className="p-xs">
					<AOPLoadFailed
						message={error}
						description="This does not mean Radar has not worked on it — the sessions could not be read."
						isRetrying={isLoading}
						onRetry={() => {
							setIsLoading(true);
							load(null, [])
								.catch(() => setError('Could not load what Radar has done on this work order.'))
								.then(() => setIsLoading(false));
						}}
					/>
				</div>
			);
		}

		return (
			<div className="p-xs">
				<div className="bg-grey-1">
					<EmptyState
						graphic={
							<img
								alt=""
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={'Radar has not worked on this work order'}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Radar picks a work order up when it matches a procedure&rsquo;s scenario.
								</div>
							</div>
						}
						height={225}
					/>
				</div>
			</div>
		);
	}

	// Newest first from the service, so the first is the session in play.
	const current = executions[0];
	const past = executions.slice(1);

	return (
		<div className="flex flex-col gap-m p-xs">
			{error ? <Alert type="error" showIcon message={error} /> : null}

			<SessionHeader
				execution={current}
				procedureName={current.procedure_name}
				timeZoneId={timeZoneId}
			/>
			<RadarSessionTimeline execution={current} timeZoneId={timeZoneId} />

			{past.length === 0 && cursor === null ? null : (
				<div className="flex flex-col bg-grey-1">
					<button
						type="button"
						className="flex cursor-pointer items-center gap-xs border-0 bg-transparent px-m py-s text-left"
						onClick={() => setArePastSessionsOpen(!arePastSessionsOpen)}
					>
						{arePastSessionsOpen ? <DownOutlined /> : <RightOutlined />}
						<span className="bodymedium text-character-primary">Past sessions</span>
						{/* `+` while a page is outstanding: the number is what has been
						    read, not what there is, and printing it bare says otherwise. */}
						<span className="bodyregular text-character-secondary">
							{past.length}
							{cursor === null ? '' : '+'}
						</span>
					</button>

					{arePastSessionsOpen
						? past.map((execution) => (
								<div key={execution.id} className="border-0 border-t border-solid border-grey-4">
									<button
										type="button"
										className="flex w-full cursor-pointer items-center border-0 bg-transparent p-0 text-left"
										onClick={() => setOpenPastId(openPastId === execution.id ? null : execution.id)}
									>
										<SessionHeader
											execution={execution}
											procedureName={execution.procedure_name}
											timeZoneId={timeZoneId}
										/>
									</button>
									{openPastId === execution.id ? (
										<RadarSessionTimeline execution={execution} timeZoneId={timeZoneId} />
									) : null}
								</div>
						  ))
						: null}

					{/*
					  The older sessions, asked for rather than pre-fetched. Only once
					  the list is open — there is nothing to add to while it is shut.
					*/}
					{arePastSessionsOpen && cursor !== null ? (
						<div className="border-0 border-t border-solid border-grey-4 px-m py-s">
							<Button size="small" loading={isLoadingMore} onClick={onLoadMore}>
								Load more
							</Button>
						</div>
					) : null}
				</div>
			)}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	/*
	  Events are stamped UTC, and whoever reads them cares what time it was at
	  the site — the parent detail page has already read the work order, so the
	  zone is there to be used rather than defaulted to the viewer's.
	*/
	timeZoneId: getWorkOrderTimeZoneId(
		getEntityById(nullSafeGet('match.params.id', ownProps), state.work_orders, null)
	),
});

const mapDispatchToProps = (dispatch) => ({
	loadExecutions: (workOrderId: number, after: string | null, limit: number) =>
		dispatch(readWorkOrderExecutions(workOrderId, { after, limit })),
	// One request, one record: `GET /scenarios` takes `procedure_id`, so the
	// procedure's name is asked for rather than found by reading the list.
});

const ConnectedRadarActivityPage = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(WorkOrdersDetailRadarActivityPage)
);

/**
 * Keyed on the work order, so stepping to the next one starts the page over.
 *
 * React Router keeps the same element across a change of `:id`, and everything
 * this page holds — the sessions, which one is open, the error — belongs to the
 * work order it was read for. Remounting on the id is the whole reset; reading
 * it again by hand would leave the open session and the error behind.
 */
const KeyedRadarActivityPage: FC<any> = (props) => (
	<ConnectedRadarActivityPage key={nullSafeGet('match.params.id', props)} {...props} />
);

export default withRouter<any, any>(KeyedRadarActivityPage);
